import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, combineLatest, of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { UserService } from '@shared/services/user.service';
import { OktaIdToken, Role, UpdateUserLoginRequest, User } from '@shared/models/users.model';
import { ResponseStatus } from '@shared/models/base/response-status.model';
import { IResponse } from '@shared/models/base/http-response.model';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { StringConstants } from '@shared/constants/strings.constants';
import { ErrorStore } from '@shared/stores/error.store';
import { ErrorModalService } from '@shared/services/error-modal.service';
import { CommonStore } from './common.store';
// import { CompanyService } from '@shared/services/company.service';
import { CompanyDetails } from '@shared/models/company.model';

export interface UserState {
  user: User;
  fetched: boolean;
  isAuthenticated: boolean;
  userStatus: ResponseStatus;
  authChecked: boolean;
  loading: boolean;
  webSubscriptionId: string | null;
  companySearchStatus: ResponseStatus;
  companyAddress: CompanyDetails;
}

const initialState: UserState = {
  user: {
    firstName: '',
    lastName: '',
    email: '',
    userId: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    companyId: '',
    companyName: '',
    features: '',
    title: '',
    department: '',
    phone: '',
    onlineNotification: false,
    emailNotification: false,
    role: undefined,
    isCompanyCommonEnabled: false,
    isTeamsViewEnabled: false,
    isCompanySynonymsEnabled: false,
    isCompanySharedSynonymsEnabled: false,
    isUserSynonymsEnabled: false,
    isUserSharedSynonymsEnabled: false,
    usingCompanyAddress: false,
    timezone: '',
    isInitialLogin: false,
    isNmfta: false,
    enableComments: false,
    enableShareComments: false,
    isDetailedSearchCompact: false,
  },
  fetched: false,
  isAuthenticated: false,
  userStatus: ResponseStatus.IDLE,
  authChecked: false,
  webSubscriptionId: null,
  companySearchStatus: ResponseStatus.IDLE,
  loading: false,
  companyAddress: {} as CompanyDetails,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withDevtools('user-info'),
  withState(initialState),
  withComputed((store) => ({
    hasUserInformation: computed(() => {
      return !!store.user()?.userId && store.isAuthenticated();
    }),
    isSuperAdmin: computed(() => {
      const currentRole = store.user()?.role;

      if (!currentRole || currentRole.trim() === '') return false;

      return [
        StringConstants.role_SuperAdmin,
      ].includes(currentRole);
    }),
    isClassificationUser: computed(() => {
      const currentRole = store.user()?.role;

      if (!currentRole || currentRole.trim() === '') return false;

      return [
        StringConstants.role_SuperAdmin,
        StringConstants.role_Classification,
        StringConstants.role_ClassificationManager,
      ].includes(currentRole);
    }),
    isClassificationManager: computed(() => {
      const currentRole = store.user()?.role;

      if (!currentRole || currentRole.trim() === '') return false;

      return [
        StringConstants.role_SuperAdmin,
        StringConstants.role_ClassificationManager,
      ].includes(currentRole);
    }),
    isInternalUser: computed(() => { // TODO: Remove this and use isClassificationUser once we hook up all the user functions
      const currentRole = store.user()?.role;

      if (!currentRole || currentRole.trim() === '') return false;

      return [
        StringConstants.role_SuperAdmin,
        StringConstants.role_Classification,
        StringConstants.role_ClassificationManager,
      ].includes(currentRole);
    }),
    userDataFields: computed(() => {
      const user = store.user();
      return [
        {
          propertyName: 'Generic_FirstName',
          propertyValue: user.firstName,
          controlName: 'firstName',
        },
        {
          propertyName: 'Generic_LastName',
          propertyValue: user.lastName,
          controlName: 'lastName',
        },
        {
          propertyName: 'Generic_CompanyName',
          propertyValue: user.companyName,
          controlName: 'companyName',
        },
        {
          propertyName: 'Generic_JobTitle',
          propertyValue: user.title,
          controlName: 'title',
        },
        {
          propertyName: 'Generic_Department',
          propertyValue: user.department,
          controlName: 'department',
        },
        {
          propertyName: 'Generic_EmailAddress',
          propertyValue: user.email,
          controlName: 'email',
        },
        {
          propertyName: 'Generic_Phone',
          propertyValue: user.phone,
          controlName: 'phone',
        },
        {
          propertyName: 'Generic_Address',
          propertyValue:
            [user.address, user.city, user.state, user.postalCode, user.country].filter(Boolean).join(', ') || '-',
          controlName: 'address',
        },
        {
          propertyName: 'Generic_Timezone',
          propertyValue: user.timezone,
          controlName: 'timezone',
        },
      ];
    }),
    userLoading: computed(() => {
      const authChecked = store.authChecked();
      const isAuthenticated = store.isAuthenticated();
      const userId = store.user.userId();
      const isLoading = store.userStatus() === 'loading';

      return !authChecked || isLoading || (isAuthenticated && !userId);
    }),
    userNotifications: computed(() => {
      const user = store.user();
      return [
        {
          propertyName: 'Generic_Online_Notification',
          propertyValue: user.onlineNotification,
          controlName: 'onlineNotification',
          itemDescription: 'Receive notifications via the Bell icon (default, can only be disabled if email is selected)',
        },
        {
          propertyName: 'Generic_Email_Notification',
          propertyValue: user.emailNotification,
          controlName: 'emailNotification',
          itemDescription: 'Receive notifications directly to your email inbox',
        },
      ];
    }),
  })),
  withMethods((
    store,
    oktaAuth = inject(OKTA_AUTH),
    userService = inject(UserService),
    errorModalService = inject(ErrorModalService),
    errorStore = inject(ErrorStore),
    // companyService = inject(CompanyService),
    commonStore = inject(CommonStore)
  ) => ({
    loadUserInfo: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { loading: true, userStatus: ResponseStatus.LOADING })),
        switchMap(() => {
          return userService.getCurrentUserProfile().pipe(
            tapResponse({
              next: (resp: IResponse<User>) => {
                if (resp.success && resp.data) {
                  patchState(store, {
                    fetched: true,
                    user: {
                      ...resp.data,
                      isInitialLogin: store.user().isInitialLogin,
                    },
                    userStatus: ResponseStatus.LOADED,
                    authChecked: true,
                    loading: false
                  });

                  if (resp.message?.includes('USER_IS_NOT_LICENSED')) {
                    errorStore.logError({ success: resp.success, code: resp.code || 'unknown', message: resp.message }, true);

                    errorModalService.showErrorModal(async () => {
                      await oktaAuth.signOut();
                    });
                  }
                  // commonStore.getVariables();
                }
              },
              error: (err) => {
                patchState(store, {
                  ...initialState,
                  userStatus: ResponseStatus.ERROR,
                });
                console.error(err);
              },
            }),
          );
        }),
      ),
    ),
    updateUserInfo: rxMethod<any>(
      pipe(
        tap(() => patchState(store, { loading: true, userStatus: ResponseStatus.LOADING })),
        switchMap((user) => {
          return userService.updateUserInfo(user).pipe(
            tapResponse({
              next: (resp: IResponse<boolean>) => {
                if (resp && resp.success) {
                  patchState(store, {
                    user: {
                      ...store.user(),
                      firstName: user.firstName,
                      lastName: user.lastName,
                      title: user.title,
                      department: user.department,
                      phone: user.phone,
                      state: user.state,
                      city: user.city,
                      address: user.address,
                      postalCode: user.postalCode,
                      country: user.country,
                      usingCompanyAddress: user.companyAddressCheckbox,
                      timezone: user.timezone,
                    },
                    userStatus: ResponseStatus.LOADED,
                    loading: false
                  });
                }
              },
              error: (err) => {
                patchState(store, {
                  user: {
                    firstName: '',
                    lastName: '',
                    email: '',
                    userId: '',
                    address: '',
                    companyId: '',
                    companyName: '',
                    features: '',
                    title: '',
                    department: '',
                    phone: '',
                    emailNotification: false,
                    onlineNotification: false,
                    role: undefined,
                    enableComments: false,
                  },
                  userStatus: ResponseStatus.ERROR,
                  loading: false
                });
                console.error(err);
              },
            }),
          );
        }),
      ),
    ),
    updateUserLogin: rxMethod<UpdateUserLoginRequest>(
      pipe(
        tap(() => patchState(store, { loading: true, userStatus: ResponseStatus.LOADING })),
        switchMap((request) => {
          return userService.updateUserLastLogin(request).pipe(
            tapResponse({
              next: (resp: IResponse<boolean>) => {
                if (resp.success && resp.data) {
                  patchState(store, {
                    userStatus: ResponseStatus.LOADED,
                    loading: false
                  });
                }
              },
              error: (err) => {
                patchState(store, {
                  userStatus: ResponseStatus.ERROR,
                  loading: false
                });
                console.error(err);
              },
            }),

            catchError(err => {
              return of(null);
            })
          );
        })
      )
    ),
    // updateCurrentUserResources(resources: UpdateUserResources) {
    //   patchState(store, {
    //     user: {
    //       ...store.user(),
    //       isCompanyCommonEnabled: resources.isCompanyCommonEnabled ?? store.user().isCompanyCommonEnabled,
    //       isTeamsViewEnabled: resources.isTeamsViewEnabled ?? store.user().isTeamsViewEnabled,
    //       isCompanySynonymsEnabled: resources.isCompanySynonymsEnabled ?? store.user().isCompanySynonymsEnabled,
    //       isCompanySharedSynonymsEnabled: resources.isCompanySharedSynonymsEnabled ?? store.user().isCompanySharedSynonymsEnabled,
    //       isUserSynonymsEnabled: resources.isUserSynonymsEnabled ?? resources.isCompanySynonymsEnabled ?? store.user().isUserSynonymsEnabled,
    //       isUserSharedSynonymsEnabled: resources.isUserSharedSynonymsEnabled ?? (resources.isCompanySharedSynonymsEnabled && resources.isCompanyCommonEnabled) ?? store.user().isUserSharedSynonymsEnabled,
    //       enableComments: resources.enableComments ?? store.user().enableComments,
    //       enableShareComments: resources.enableShareComments ?? store.user().enableShareComments,
    //     },
    //     userStatus: ResponseStatus.LOADED,
    //     loading: false
    //   });
    // },
    clearUserState: () => {
      patchState(store, { ...initialState });
    }
  })),
  withMethods((store, oktaAuth = inject(OKTA_AUTH), authStateService = inject(OktaAuthStateService)) => ({
    initAuth: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, userStatus: ResponseStatus.LOADING, authChecked: false })),
        switchMap(() => {
          return combineLatest([authStateService.authState$, of(oktaAuth)]);
        }),
        switchMap(async ([authState, okta]) => {
          if (authState?.isAuthenticated) {
            patchState(store, { isAuthenticated: true });
            const user = await okta.getUser();
            //Store new logins time for login
            const idToken = await oktaAuth.tokenManager.get('idToken') as OktaIdToken;
            let isInitialLogin = false;
            if (idToken) {
              const loginDateTime = new Date(idToken.claims?.auth_time! * 1000)
              const currentDateTime = new Date();
              isInitialLogin = (currentDateTime.getTime() - loginDateTime.getTime() < 30000);
              // Commented this out for now, as we are not using this endpoint yet. We may just leave it off since this is an internal app
              // if (isInitialLogin)
              //   store.updateUserLogin({ userEmail: user.email!, loginTime: loginDateTime })
            }

            patchState(store, {
              userStatus: ResponseStatus.LOADED,
              user: {
                ...store.user(),
                isInitialLogin: isInitialLogin
              },
              fetched: true,
              authChecked: true,
              loading: false,
            });

            return user?.preferred_username || null;
          } else {
            patchState(store, { isAuthenticated: false });
            return null;
          }
        }),
        tapResponse({
          next: (preferred_username: string | null) => {
            if (preferred_username) {
              store.loadUserInfo(preferred_username);

              patchState(store, {
                user: {
                  ...store.user(),
                  isInitialLogin: store.user().isInitialLogin
                }
              });
            } else {
              patchState(store, {
                userStatus: ResponseStatus.LOADED,
                fetched: true,
                authChecked: true,
                loading: false
              });
            }
          },
          error: (err) => {
            console.error('Error in verifying authentication', err);
            patchState(store, {
              userStatus: ResponseStatus.ERROR,
              fetched: true,
              authChecked: true,
              loading: false
            });
          },
        }),
      ),
    ),

  })),
);
